import { useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import { PortalStyleConfig, ThemeColorSchemas } from '@nx-frontend/shared/types';
import { ChatbotContext } from './chatbot-context';
import { ChatbotThreadComponent } from './chatbot-thread';
import { $aiThreads } from './store';

dayjs.extend(relativeTime);
dayjs.extend(isBetween);

interface ChatbotHistoryProps {
  onSelectThread: (id: string) => void;
  onThreadDelete: (id: string) => void;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
}

export function ChatbotHistory({ onSelectThread, onThreadDelete, themeColors, themeStyle }: ChatbotHistoryProps) {
  const aiContext = useContext(ChatbotContext);
  const [aiThreads] = useUnit([$aiThreads]);

  const t = useTranslations();

  const historyGroups: { [group: string]: (date: Dayjs) => boolean } = useMemo(
    () => ({
      [t('features.ai-helper.today')]: (date) => date.isAfter(dayjs().startOf('day')),
      [t('features.ai-helper.yesterday')]: (date) =>
        date.isBetween(dayjs().startOf('day').subtract(1, 'day'), dayjs().startOf('day')),
      [t('features.ai-helper.previous_seven_days')]: (date) =>
        date.isBetween(dayjs().startOf('day').subtract(8, 'day'), dayjs().startOf('day').subtract(1, 'day')),
      [t('features.ai-helper.previous_thirty_days')]: (date) =>
        date.isBetween(dayjs().startOf('day').subtract(31, 'day'), dayjs().startOf('day').subtract(8, 'day')),
    }),
    [t]
  );

  const workspaceThreadsIds = useMemo(
    () =>
      Object.keys(aiThreads.threads).filter(
        (threadId) => aiThreads.threads[threadId].workspaceId === aiContext.workspaceId
      ),
    [aiContext.workspaceId, aiThreads.threads]
  );

  /* Group threads and sort */
  const threadsIdsByGroups = useMemo(() => {
    const result = workspaceThreadsIds.reduce((threadsIds, id) => {
      // in case timestamp is in ms, we need to convert it to seconds
      // compare with 2030 year date
      let threadTimestamp = aiThreads.threads[id].updatedAt / 1000;
      if (threadTimestamp > 1893456000) {
        threadTimestamp /= 1000;
      }

      const threadDate = dayjs.unix(threadTimestamp);
      const threadGroup = Object.keys(historyGroups).find((group) => historyGroups[group](threadDate));
      if (threadGroup) {
        if (!threadsIds[threadGroup]) {
          threadsIds[threadGroup] = [];
        }
        threadsIds[threadGroup].push(id);
        return threadsIds;
      }
      const group = threadDate.isSame(dayjs(), 'year')
        ? t(`shared.months.${threadDate.format('MMMM').toLowerCase()}`)
        : threadDate.format('YYYY');
      if (!threadsIds[group]) {
        threadsIds[group] = [];
      }
      threadsIds[group].push(id);
      return threadsIds;
    }, {} as { [group: string]: string[] });

    Object.keys(result).forEach((group) => {
      result[group] = result[group].sort((a, b) =>
        aiThreads.threads[a].updatedAt > aiThreads.threads[b].updatedAt ? -1 : 1
      );
    });
    return result;
  }, [aiThreads.threads, historyGroups, workspaceThreadsIds, t]);

  const groups = useMemo(() => {
    return Object.keys(threadsIdsByGroups).sort((a, b) =>
      aiThreads.threads[threadsIdsByGroups[a][0]].updatedAt > aiThreads.threads[threadsIdsByGroups[b][0]].updatedAt
        ? -1
        : 1
    );
  }, [aiThreads.threads, threadsIdsByGroups]);

  return (
    <div className="overflow-y-auto grow p-6 overflow-x-hidden">
      {groups.map((group) => (
        <div className="flex flex-col gap-2 mb-10" key={group}>
          <h2 className="uppercase text-neutral-500 text-[10px] font-bold mb-2">{group}</h2>
          {threadsIdsByGroups[group].map((threadId) => (
            <ChatbotThreadComponent
              key={threadId}
              id={threadId}
              title={aiThreads.threads[threadId].title}
              date={aiThreads.threads[threadId].updatedAt}
              onClick={onSelectThread}
              onDelete={onThreadDelete}
              themeColors={themeColors}
              themeStyle={themeStyle}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
